(function () {
  'use strict';

  function isShopifyDiscountUrl(url) {
    try {
      const pathname = new URL(String(url), window.location.origin).pathname;
      return /^\/discount\/[^/]+/i.test(pathname);
    } catch (error) {
      return /\/discount\/[^/?#]+/i.test(String(url || ''));
    }
  }

  function shouldBlockShopifyDiscountUrl() {
    return Boolean(
      window.__sbbCouponFlowActive ||
        window.__sbbAwaitingManualDiscount ||
        window.SbbCouponFlow?.isFlowActive?.() ||
        window.SbbCouponFlow?.isAwaitingManualDiscount?.(),
    );
  }

  function installShopifyDiscountUrlGuard() {
    const originalFetch = window.fetch.bind(window);
    window.fetch = function sbbDiscountUrlGuardedFetch(input, init) {
      const url = typeof input === 'string' ? input : input?.url || String(input);
      const method = String(init?.method || 'GET').toUpperCase();

      if (shouldBlockShopifyDiscountUrl() && method === 'GET' && isShopifyDiscountUrl(url)) {
        return Promise.resolve(
          new Response(null, {
            status: 204,
            statusText: 'Blocked by SbbCouponFlow',
          }),
        );
      }

      return originalFetch(input, init);
    };

    if (window.__sbbShopifyDiscountUrlGuardInstalled) {
      return;
    }

    window.__sbbShopifyDiscountUrlGuardInstalled = true;

    const originalXhrOpen = XMLHttpRequest.prototype.open;
    const originalXhrSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function patchedSbbDiscountGuardOpen(method, url, ...rest) {
      this.__sbbDiscountGuardMethod = String(method || 'GET').toUpperCase();
      this.__sbbDiscountGuardUrl = String(url || '');
      return originalXhrOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function patchedSbbDiscountGuardSend(body) {
      const method = this.__sbbDiscountGuardMethod || 'GET';
      const url = this.__sbbDiscountGuardUrl || '';

      if (shouldBlockShopifyDiscountUrl() && method === 'GET' && isShopifyDiscountUrl(url)) {
        return undefined;
      }

      return originalXhrSend.call(this, body);
    };

    document.addEventListener(
      'click',
      (event) => {
        if (!shouldBlockShopifyDiscountUrl()) return;

        const anchor = event.target?.closest?.('a[href]');
        if (!anchor) return;

        const href = anchor.getAttribute('href') || '';
        if (!isShopifyDiscountUrl(href)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true,
    );
  }

  installShopifyDiscountUrlGuard();

  const SAVINGS_EPSILON = 0.001;

  const DiscountEngine = (() => {
    const CENTS_THRESHOLD = 10000;

    function toNumber(value, fallback = 0) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    }

    function normalizeTier(rawTier = {}) {
      const minimumRaw = toNumber(rawTier.minimum ?? rawTier.minimumAmount, 0);
      const discountRaw = toNumber(rawTier.discountValue, 0);

      return {
        minimum: minimumRaw >= CENTS_THRESHOLD ? minimumRaw : Math.round(minimumRaw * 100),
        discountType: rawTier.discountType === 'percentage' ? 'percentage' : 'fixed',
        discountValue: discountRaw >= CENTS_THRESHOLD ? discountRaw / 100 : discountRaw,
        label: rawTier.label || '',
        message: rawTier.message || '',
      };
    }

    function normalizeTiers(rawTiers = []) {
      return (Array.isArray(rawTiers) ? rawTiers : [])
        .map((tier) => normalizeTier(tier))
        .sort((left, right) => left.minimum - right.minimum);
    }

    function getTierState(tiers = [], cartSubtotalCents = 0) {
      const normalizedTotal = Math.max(0, toNumber(cartSubtotalCents, 0));
      let activeTier = null;
      let nextTier = null;

      for (const tier of tiers) {
        if (normalizedTotal >= tier.minimum) {
          activeTier = tier;
          continue;
        }

        nextTier = tier;
        break;
      }

      return {
        activeTier,
        nextTier,
        remainingToNext: nextTier ? Math.max(0, nextTier.minimum - normalizedTotal) : 0,
        isTopTierReached: Boolean(activeTier) && !nextTier,
      };
    }

    function calculateTierSavings(tiers, cartSubtotalCents) {
      const subtotalMajor = Math.max(0, toNumber(cartSubtotalCents, 0)) / 100;
      let activeTier = null;

      for (const tier of tiers) {
        if (subtotalMajor >= tier.minimum / 100) {
          activeTier = tier;
        }
      }

      if (!activeTier) return 0;

      if (activeTier.discountType === 'percentage') {
        return Math.round(((subtotalMajor * activeTier.discountValue) / 100) * 100) / 100;
      }

      return Math.min(activeTier.discountValue, subtotalMajor);
    }

    function getCompareAtSavings(cart) {
      const items = cart?.items;
      if (!Array.isArray(items) || !items.length) return 0;

      return items.reduce((sum, item) => {
        const quantity = Math.max(0, toNumber(item.quantity, 0));
        if (!quantity) return sum;

        const priceCents = toNumber(item.price, 0);
        const compareAtCents = toNumber(item.compare_at_price, 0);
        if (compareAtCents > priceCents) {
          return sum + (compareAtCents - priceCents) * quantity;
        }

        return sum;
      }, 0);
    }

    function getLineOriginalUnitCents(item) {
      if (window.UdbVariantPrice?.getLineAdminVariantUnitCents) {
        return window.UdbVariantPrice.getLineAdminVariantUnitCents(item);
      }

      const quantity = Math.max(1, toNumber(item?.quantity, 0));
      const originalLineCents = toNumber(item?.original_line_price, 0);
      if (originalLineCents > 0) {
        return Math.round(originalLineCents / quantity);
      }

      const originalCents = toNumber(item?.original_price, 0);
      if (originalCents > 0) return originalCents;

      return toNumber(item?.price, 0);
    }

    function getLineCatalogSubtotalCents(item) {
      if (window.UdbVariantPrice?.getLineAdminVariantLineCents) {
        return window.UdbVariantPrice.getLineAdminVariantLineCents(item);
      }

      const originalLineCents = toNumber(item?.original_line_price, 0);
      if (originalLineCents > 0) return originalLineCents;

      const quantity = Math.max(0, toNumber(item?.quantity, 0));
      if (!quantity) return 0;

      return getLineOriginalUnitCents(item) * quantity;
    }

    function getTierBasisSubtotalCents(cart) {
      window.UdbVariantPrice?.seedCacheFromCart?.(cart);

      if (window.UdbVariantPrice?.getTierBasisSubtotalCents) {
        return window.UdbVariantPrice.getTierBasisSubtotalCents(cart);
      }

      const items = cart?.items;
      if (!Array.isArray(items) || !items.length) {
        return toNumber(cart?.items_subtotal_price, 0);
      }

      return items.reduce((sum, item) => sum + getLineCatalogSubtotalCents(item), 0);
    }

    function getCartLevelDiscountSavings(cart) {
      const applications = cart?.cart_level_discount_applications;
      if (Array.isArray(applications) && applications.length) {
        return applications.reduce(
          (sum, application) => sum + toNumber(application.total_allocated_amount, 0),
          0,
        );
      }

      const baseSubtotalCents = toNumber(cart?.items_subtotal_price, 0);
      const totalCents = toNumber(cart?.total_price, 0);
      return Math.max(0, baseSubtotalCents - totalCents);
    }

    function getCartLevelSavings(cart) {
      const baseSubtotalCents = toNumber(cart?.items_subtotal_price, 0);
      const tierBasisSubtotalCents = getTierBasisSubtotalCents(cart);
      const currentTotalCents = toNumber(cart?.total_price, 0) || baseSubtotalCents;
      const compareAtSavingsCents = getCompareAtSavings(cart);
      const cartLevelSavingsCents = getCartLevelDiscountSavings(cart);
      const existingSavingsCents = cartLevelSavingsCents;

      return {
        baseSubtotalCents,
        tierBasisSubtotalCents,
        compareAtSavingsCents,
        cartLevelSavingsCents,
        existingSavingsCents,
        currentTotalCents,
      };
    }

    function getAppliedDiscountCode(cart) {
      const codes = cart?.discount_codes;
      if (!Array.isArray(codes)) return '';

      const applied = codes.find((entry) => entry.applicable !== false);
      return applied?.code ? String(applied.code).trim() : '';
    }

    function compareDiscountOptions(tiers, cart) {
      const savings = getCartLevelSavings(cart);
      const baseSubtotal = savings.baseSubtotalCents / 100;
      const tierBasisSubtotal = savings.tierBasisSubtotalCents / 100;
      const compareAtSavings = savings.compareAtSavingsCents / 100;
      const cartLevelSavings = savings.cartLevelSavingsCents / 100;
      const existingSavings = savings.existingSavingsCents / 100;
      const currentTotal = savings.currentTotalCents / 100;
      const udbSavings = calculateTierSavings(tiers, savings.tierBasisSubtotalCents);
      const projectedTotal = Math.max(0, tierBasisSubtotal - udbSavings);
      const beatsExistingSavings = udbSavings > existingSavings + SAVINGS_EPSILON;
      const beatsCurrentTotal = projectedTotal < currentTotal - SAVINGS_EPSILON;
      const shouldApplyUdb = beatsExistingSavings && beatsCurrentTotal;

      return {
        baseSubtotal,
        tierBasisSubtotal,
        compareAtSavings,
        cartLevelSavings,
        existingSavings,
        udbSavings,
        shouldApplyUdb,
        currentTotal,
        projectedTotal,
        winner: shouldApplyUdb ? 'udb' : existingSavings > SAVINGS_EPSILON ? 'existing' : 'none',
      };
    }

    return {
      normalizeTiers,
      getTierState,
      getCompareAtSavings,
      getCartLevelSavings,
      getTierBasisSubtotalCents,
      getAppliedDiscountCode,
      compareDiscountOptions,
      calculateTierSavings,
    };
  })();

  async function fetchCart() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error('Unable to fetch cart');
    }

    return response.json();
  }

  async function saveCouponToCart(config, code) {
    if (!code || !config.cartAttributeKey) return false;

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          [config.cartAttributeKey]: code,
        },
      }),
    });

    return response.ok;
  }

  async function applyDiscountCode(code) {
    if (!code) return false;

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ discount: code }),
    });

    return response.ok;
  }

  async function removeDiscountCode() {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ discount: '' }),
    });

    return response.ok;
  }

  async function copyToClipboard(value) {
    if (!value) return false;

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(value);
        return true;
      } catch (error) {
        // fallback below
      }
    }

    const helper = document.createElement('textarea');
    helper.value = value;
    helper.setAttribute('readonly', 'readonly');
    helper.style.position = 'absolute';
    helper.style.left = '-9999px';
    document.body.appendChild(helper);
    helper.select();

    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (error) {
      copied = false;
    }

    helper.remove();
    return copied;
  }

  const AOV_DISCOUNT_LIST_SELECTOR = '[class*="AOV-CDMinimal__DiscountSection__List"]';

  function beginCouponFlow() {
    window.__sbbCouponFlowActive = true;
    window.__sbbAwaitingManualDiscount = false;
    suppressAovDiscountList();
    document.dispatchEvent(new CustomEvent('sbb:coupon-flow-start'));
  }

  function markAwaitingManualDiscount() {
    window.__sbbCouponFlowActive = false;
    window.__sbbAwaitingManualDiscount = true;
    document.dispatchEvent(new CustomEvent('sbb:coupon-flow-end'));
  }

  function endCouponFlow() {
    window.__sbbCouponFlowActive = false;
    window.__sbbAwaitingManualDiscount = false;
    releaseAovDiscountList();
    document.dispatchEvent(new CustomEvent('sbb:coupon-flow-end'));
  }

  function suppressAovDiscountList() {
    const drawer = document.getElementById('aov-cart-drawer');
    if (!drawer) return;

    drawer.querySelectorAll(AOV_DISCOUNT_LIST_SELECTOR).forEach((node) => {
      if (node.dataset.sbbDiscountListSuppressed === 'true') return;
      node.dataset.sbbDiscountListSuppressed = 'true';
      node.dataset.sbbDiscountListPrevDisplay = node.style.display || '';
      node.style.setProperty('display', 'none', 'important');
    });
  }

  function releaseAovDiscountList() {
    const drawer = document.getElementById('aov-cart-drawer');
    if (!drawer) return;

    drawer
      .querySelectorAll(`${AOV_DISCOUNT_LIST_SELECTOR}[data-sbb-discount-list-suppressed="true"]`)
      .forEach((node) => {
        const previous = node.dataset.sbbDiscountListPrevDisplay;
        if (previous) {
          node.style.display = previous;
        } else {
          node.style.removeProperty('display');
        }
        delete node.dataset.sbbDiscountListSuppressed;
        delete node.dataset.sbbDiscountListPrevDisplay;
      });
  }

  function cartHasAppliedDiscount(cart) {
    const applications = cart?.cart_level_discount_applications;
    if (Array.isArray(applications) && applications.length) {
      return true;
    }

    const codes = cart?.discount_codes;
    return Array.isArray(codes) && codes.some((entry) => entry.applicable !== false);
  }

  function completeManualDiscountApply(cart) {
    if (!cartHasAppliedDiscount(cart)) {
      return false;
    }

    window.__sbbAwaitingManualDiscount = false;
    releaseAovDiscountList();
    return true;
  }

  window.SbbCouponFlow = {
    beginCouponFlow,
    markAwaitingManualDiscount,
    endCouponFlow,
    suppressAovDiscountList,
    releaseAovDiscountList,
    completeManualDiscountApply,
    isFlowActive: () => Boolean(window.__sbbCouponFlowActive),
    isAwaitingManualDiscount: () => Boolean(window.__sbbAwaitingManualDiscount),
    shouldBlockShopifyDiscountUrl,
    isShopifyDiscountUrl,
  };

  function setStatus(block, message) {
    const status = block.querySelector('[data-sbb-status]');
    if (!status) return;
    status.textContent = message || '';
  }

  function updateActiveCard(block, activeTier) {
    block.querySelectorAll('[data-sbb-tier-card]').forEach((card) => {
      const minimum = Number(card.getAttribute('data-tier-minimum') || 0);
      const isActive = Boolean(activeTier) && minimum === Number(activeTier.minimum);
      card.classList.toggle('is-active', isActive);
    });
  }

  function formatMoney(cents, currencyCode) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currencyCode || 'EUR',
      }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return `${((Number(cents) || 0) / 100).toFixed(2)} ${currencyCode || ''}`.trim();
    }
  }

  function formatTierLabel(tier, currencyCode) {
    if (!tier) return '';
    if (tier.label) return tier.label;

    if (tier.discountType === 'percentage') {
      return `${tier.discountValue}%`;
    }

    return formatMoney(Math.round(tier.discountValue * 100), currencyCode);
  }

  function getCartSubtotalCents(cart) {
    return DiscountEngine.getTierBasisSubtotalCents(cart);
  }

  const STATUS_LOCK_MS = 10000;

  function applyMessageTemplate(template, values) {
    let message = String(template || '');
    Object.entries(values).forEach(([token, value]) => {
      message = message.replaceAll(`[${token}]`, value);
    });
    return message;
  }

  function lockStatus(block) {
    block.dataset.sbbStatusLock = String(Date.now() + STATUS_LOCK_MS);
  }

  function isStatusLocked(block) {
    const lockUntil = Number(block?.dataset?.sbbStatusLock || 0);
    return lockUntil > Date.now();
  }

  async function updateTierCards(block, config, tiers) {
    try {
      const cart = await fetchCart();
      const tierState = DiscountEngine.getTierState(tiers, getCartSubtotalCents(cart));
      updateActiveCard(block, tierState.activeTier);
      return tierState;
    } catch (error) {
      return null;
    }
  }

  async function updateTierMessage(block, config, tiers) {
    if (isStatusLocked(block)) {
      await updateTierCards(block, config, tiers);
      return;
    }

    try {
      const cart = await fetchCart();
      const tierState = DiscountEngine.getTierState(tiers, getCartSubtotalCents(cart));
      updateActiveCard(block, tierState.activeTier);

      if (tierState.isTopTierReached) {
        setStatus(block, config.progressCompleteText);
        return;
      }

      if (tierState.nextTier) {
        const remaining = formatMoney(tierState.remainingToNext, config.currency);
        const minimum = formatMoney(tierState.nextTier.minimum, config.currency);
        const label = formatTierLabel(tierState.nextTier, config.currency);
        const template = config.progressNextText || '';

        setStatus(
          block,
          template
            .replaceAll('[remaining]', remaining)
            .replaceAll('[minimum]', minimum)
            .replaceAll('[label]', label),
        );
        return;
      }

      setStatus(block, config.progressInitialText || '');
    } catch (error) {
      setStatus(block, config.progressInitialText || '');
    }
  }

  function getDiscountLink(code, shopUrl) {
    const redirect = shopUrl || '/collections/all';
    return `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect)}`;
  }

  function parseConfig(block) {
    const configNode = block.querySelector('script[data-sbb-config]');
    if (!configNode) return null;

    try {
      return JSON.parse(configNode.textContent || '{}');
    } catch (error) {
      return null;
    }
  }

  async function revealCoupon(block, config, tiers, code) {
    const applyMode = String(config.couponApplyMode || 'copy_only').toLowerCase();
    const copyOnly = applyMode !== 'auto';

    if (copyOnly) {
      let resultMessage = '';
      let betterDiscountActive = false;

      try {
        const cart = await fetchCart();
        const comparison = DiscountEngine.compareDiscountOptions(tiers, cart);
        betterDiscountActive = comparison.winner === 'existing';
      } catch (error) {
        betterDiscountActive = false;
      }

      const copied = await copyToClipboard(code);

      if (betterDiscountActive && copied && config.betterDiscountMessage) {
        resultMessage = applyMessageTemplate(config.betterDiscountMessage, { code });
      } else if (copied && config.copyOnlyMessage) {
        resultMessage = applyMessageTemplate(config.copyOnlyMessage, { code });
      } else if (!copied && config.errorMessage) {
        resultMessage = config.errorMessage;
      }

      lockStatus(block);
      setStatus(block, resultMessage);
      await updateTierCards(block, config, tiers);
      return;
    }

    let cartSaved = false;
    let checkoutPrepared = false;
    let betterDiscountActive = false;

    try {
      const cart = await fetchCart();
      const comparison = DiscountEngine.compareDiscountOptions(tiers, cart);
      const appliedCode = DiscountEngine.getAppliedDiscountCode(cart);
      const normalizedCode = code.toUpperCase();

      if (config.checkoutMode === 'attribute') {
        if (comparison.shouldApplyUdb) {
          cartSaved = await saveCouponToCart(config, code);
        } else {
          betterDiscountActive = comparison.winner === 'existing';
        }
      } else if (config.checkoutMode === 'discount') {
        if (comparison.shouldApplyUdb) {
          cartSaved = await applyDiscountCode(code);
        } else if (appliedCode && appliedCode.toUpperCase() === normalizedCode) {
          await removeDiscountCode();
          betterDiscountActive = true;
        } else {
          betterDiscountActive = comparison.winner === 'existing';
        }
      } else if (comparison.shouldApplyUdb) {
        const discountUrl = getDiscountLink(code, config.shopButtonUrl);
        block.setAttribute('data-sbb-discount-link', discountUrl);
        checkoutPrepared = true;
      } else {
        betterDiscountActive = comparison.winner === 'existing';
      }
    } catch (error) {
      cartSaved = false;
      checkoutPrepared = false;
    }

    const copied = await copyToClipboard(code);

    let resultMessage = '';

    if (betterDiscountActive && copied && config.betterDiscountMessage) {
      resultMessage = applyMessageTemplate(config.betterDiscountMessage, { code });
    } else if (cartSaved && copied && config.successMessage) {
      resultMessage = applyMessageTemplate(config.successMessage, { code });
    } else if ((cartSaved || checkoutPrepared) && config.pendingMessage) {
      resultMessage = applyMessageTemplate(config.pendingMessage, { code });
    } else if (copied && config.copyOnlyMessage) {
      resultMessage = applyMessageTemplate(config.copyOnlyMessage, { code });
    } else if (!copied && config.errorMessage) {
      resultMessage = config.errorMessage;
    }

    lockStatus(block);
    setStatus(block, resultMessage);
    await updateTierCards(block, config, tiers);
    document.dispatchEvent(new CustomEvent('cart:change', { detail: { source: 'universal-tier-discount' } }));
  }

  async function runKlaviyoGate(block, config) {
    if (!config?.klaviyo?.enabled) {
      return 'disabled';
    }

    if (!window.SbbKlaviyoGate?.waitForSubmission) {
      if (config.klaviyo.fallbackOnFailure) {
        return 'fallback';
      }
      throw new Error('Klaviyo popup helper is not loaded');
    }

    setStatus(block, config.klaviyo?.waitingMessage || '');
    lockStatus(block);
    return window.SbbKlaviyoGate.waitForSubmission(config.klaviyo);
  }

  async function completeKlaviyoCouponFlow(block, config) {
    lockStatus(block);
    setStatus(block, config.klaviyo?.postSubmitMessage || '');
    markAwaitingManualDiscount();
  }

  function handleKlaviyoGateFailure(block, config, gateResult) {
    if (gateResult === 'fallback') {
      if (!config.klaviyo?.fallbackOnFailure) {
        lockStatus(block);
        setStatus(block, config.klaviyo?.cancelledMessage || '');
        return true;
      }

      if (config.klaviyo?.fallbackMessage) {
        lockStatus(block);
        setStatus(block, config.klaviyo.fallbackMessage);
      }
      return true;
    }

    if (
      gateResult === 'submitted' &&
      config.klaviyo?.skipIfAlreadySubmitted &&
      config.klaviyo?.reopenForCodeCopy === false
    ) {
      lockStatus(block);
      setStatus(
        block,
        config.klaviyo?.skippedMessage || config.klaviyo?.postSubmitMessage || '',
      );
      markAwaitingManualDiscount();
      return true;
    }

    if (gateResult !== 'submitted' && gateResult !== 'reopened') {
      lockStatus(block);
      setStatus(block, config.klaviyo?.cancelledMessage || '');
      return true;
    }

    return false;
  }

  async function onCouponClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    const block = button.closest('[data-sbb-block]');
    if (!block) return;

    const config = parseConfig(block);
    if (!config || !config.code) return;

    button.disabled = true;
    beginCouponFlow();

    try {
      if (!config.klaviyo?.enabled) {
        lockStatus(block);
        setStatus(block, config.klaviyo?.disabledMessage || '');
        endCouponFlow();
        return;
      }

      if (window.SbbKlaviyoGate?.preload) {
        await window.SbbKlaviyoGate.preload();
      }

      const gateResult = await runKlaviyoGate(block, config);

      if (handleKlaviyoGateFailure(block, config, gateResult)) {
        if (
          gateResult === 'submitted' &&
          config.klaviyo?.skipIfAlreadySubmitted &&
          config.klaviyo?.reopenForCodeCopy === false
        ) {
          markAwaitingManualDiscount();
        } else {
          endCouponFlow();
        }
        return;
      }

      await completeKlaviyoCouponFlow(block, config);
    } catch (error) {
      endCouponFlow();
      lockStatus(block);
      const openFailed =
        error?.message && String(error.message).includes('Klaviyo popup did not open');
      setStatus(
        block,
        openFailed
          ? config.klaviyo?.openFailedMessage || ''
          : config.klaviyo?.cancelledMessage || '',
      );
    } finally {
      button.disabled = false;
    }
  }

  function initBlock(block) {
    if (block.dataset.sbbReady === 'true') return;

    const config = parseConfig(block);
    if (!config || config.enabled === false) return;

    const tiers = DiscountEngine.normalizeTiers(config.tiers || []);
    updateTierMessage(block, config, tiers);

    const couponButton = block.querySelector('[data-sbb-get-coupon]');
    if (couponButton) {
      couponButton.addEventListener('click', onCouponClick);
    }

    block.addEventListener('sbb:refresh', () => {
      updateTierMessage(block, config, tiers);
    });

    document.addEventListener(
      'sbb:klaviyo-opened',
      () => {
        block.dataset.sbbKlaviyoPopupOpened = 'true';
        if (window.SbbCouponFlow?.isFlowActive?.()) {
          suppressAovDiscountList();
        }
      },
      { once: true },
    );

    block.dataset.sbbReady = 'true';

    if (config.klaviyo?.enabled && window.SbbKlaviyoGate?.preload) {
      window.SbbKlaviyoGate.preload();
    }
  }

  function watchAovDiscountListSuppression() {
    const drawer = document.getElementById('aov-cart-drawer');
    if (!drawer || drawer.dataset.sbbDiscountListWatch) return;

    drawer.dataset.sbbDiscountListWatch = 'true';
    new MutationObserver(() => {
      if (
        window.SbbCouponFlow?.isFlowActive?.() ||
        window.SbbCouponFlow?.isAwaitingManualDiscount?.()
      ) {
        suppressAovDiscountList();
      }
    }).observe(drawer, { childList: true, subtree: true });
  }

  function init() {
    installShopifyDiscountUrlGuard();
    watchAovDiscountListSuppression();
    document.querySelectorAll('[data-sbb-block]').forEach((block) => {
      initBlock(block);
    });
  }

  document.addEventListener('cart:change', (event) => {
    const cart = event?.detail?.cart;

    if (cart && window.SbbCouponFlow?.isAwaitingManualDiscount?.()) {
      window.SbbCouponFlow.completeManualDiscountApply(cart);
    }

    if (window.SbbCouponFlow?.isFlowActive?.()) {
      return;
    }

    if (event?.detail?.source === 'universal-tier-discount') {
      return;
    }

    document.querySelectorAll('[data-sbb-block][data-sbb-ready="true"]').forEach((block) => {
      block.dispatchEvent(new CustomEvent('sbb:refresh'));
    });
  });

  document.addEventListener('sbb:coupon-flow-start', () => {
    suppressAovDiscountList();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
